import React, { useEffect, useState, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPinIcon, ListIcon, HeartHandshakeIcon, UserIcon, UsersIcon, FlameIcon, HeartIcon, BookOpenIcon, AlertCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
// Mock data for settings tags
const settingsTags = [{
  id: 'small-town',
  name: 'Small Town',
  description: 'Stories set in small, close-knit communities where everyone knows each other.',
  count: 183,
  category: 'Contemporary'
}, {
  id: 'big-city',
  name: 'Big City',
  description: 'Stories set in large metropolitan areas with diverse populations and urban environments.',
  count: 167,
  category: 'Contemporary'
}, {
  id: 'rural',
  name: 'Rural',
  description: 'Stories set in countryside or farmland areas, away from urban centers.',
  count: 132,
  category: 'Contemporary'
}, {
  id: 'coastal',
  name: 'Coastal',
  description: 'Stories set in seaside towns, beaches, or oceanfront communities.',
  count: 145,
  category: 'Contemporary'
}, {
  id: 'island',
  name: 'Island',
  description: 'Stories set on islands, often with themes of isolation or close community.',
  count: 87,
  category: 'Contemporary'
}, {
  id: 'university',
  name: 'University/College',
  description: 'Stories set on university or college campuses, often featuring student life.',
  count: 156,
  category: 'Contemporary'
}, {
  id: 'high-school',
  name: 'High School',
  description: 'Stories set in high schools, often coming-of-age narratives.',
  count: 134,
  category: 'Contemporary'
}, {
  id: 'medieval',
  name: 'Medieval',
  description: 'Stories set in medieval periods, typically featuring castles, knights, and feudal systems.',
  count: 142,
  category: 'Historical'
}, {
  id: 'regency',
  name: 'Regency',
  description: 'Stories set in the British Regency era (early 19th century).',
  count: 128,
  category: 'Historical'
}, {
  id: 'victorian',
  name: 'Victorian',
  description: 'Stories set in the Victorian era, typically featuring strict social norms and industrial revolution.',
  count: 112,
  category: 'Historical'
}, {
  id: 'ancient-rome',
  name: 'Ancient Rome',
  description: 'Stories set in the Roman Empire period.',
  count: 76,
  category: 'Historical'
}, {
  id: 'wild-west',
  name: 'Wild West',
  description: 'Stories set in the American frontier during the westward expansion.',
  count: 94,
  category: 'Historical'
}, {
  id: 'high-fantasy',
  name: 'High Fantasy World',
  description: 'Stories set in completely fictional worlds with their own geography and rules.',
  count: 178,
  category: 'Fantasy'
}, {
  id: 'urban-fantasy',
  name: 'Urban Fantasy',
  description: 'Stories set in modern urban environments but with fantasy elements.',
  count: 165,
  category: 'Fantasy'
}, {
  id: 'space',
  name: 'Space',
  description: 'Stories set in outer space, often aboard spaceships or space stations.',
  count: 132,
  category: 'Science Fiction'
}, {
  id: 'dystopian',
  name: 'Dystopian',
  description: 'Stories set in societies characterized by oppression, disaster, or decline.',
  count: 154,
  category: 'Science Fiction'
}, {
  id: 'post-apocalyptic',
  name: 'Post-Apocalyptic',
  description: 'Stories set after a global catastrophe has destroyed most of civilization.',
  count: 138,
  category: 'Science Fiction'
}];
const Settings: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || 'all');
  const [sortOption, setSortOption] = useState<'alphabetical' | 'bookCount' | 'viewAll'>('alphabetical');
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  // Helper function to get color based on setting category
  const getSettingColor = (category: string) => {
    if (darkMode) {
      switch (category) {
        case 'Contemporary':
          return 'bg-green-900/30 text-green-300';
        case 'Historical':
          return 'bg-amber-900/30 text-amber-300';
        case 'Fantasy':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'Science Fiction':
          return 'bg-blue-900/30 text-blue-300';
        default:
          return dk.badgeNeutral;
      }
    }
    switch (category) {
      case 'Contemporary':
        return 'bg-green-100 text-green-900';
      case 'Historical':
        return 'bg-amber-100 text-amber-900';
      case 'Fantasy':
        return 'bg-indigo-100 text-indigo-900';
      case 'Science Fiction':
        return 'bg-blue-100 text-blue-900';
      default:
        return 'bg-gray-100 text-gray-900';
    }
  };
  // Group tags by category
  const groupedTags = settingsTags.reduce((acc, tag) => {
    const category = tag.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tag);
    return acc;
  }, {} as Record<string, typeof settingsTags>);
  // Filter tags based on search term and selected category
  const filteredTags = settingsTags.filter(tag => {
    const matchesSearch = !searchQuery || tag.name.toLowerCase().includes(searchQuery.toLowerCase()) || tag.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || tag.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  // Sort filtered tags
  const sortedTags = [...filteredTags].sort((a, b) => {
    if (sortOption === 'bookCount') {
      return b.count - a.count;
    }
    return a.name.localeCompare(b.name);
  });
  // Re-group for display if needed
  const displayGroupedTags = sortedTags.reduce((acc, tag) => {
    const category = tag.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(tag);
    return acc;
  }, {} as Record<string, typeof settingsTags>);
  const categories = Array.from(new Set(settingsTags.map(t => t.category)));
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Representation',
    href: '/representation',
    icon: HeartIcon
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    isActive: true,
    subItems: categories.map(c => ({
      label: c,
      href: `/settings?category=${encodeURIComponent(c)}`,
      isActive: selectedCategory === c
    }))
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const renderSettings = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchQuery) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-13756" data-mp="ptszabiqc8pf">
          {sortedTags.map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getSettingColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer`} data-id="element-13757" data-mp="gv7m6vw11lw1">
              <div className="flex justify-between items-center mb-1" data-id="element-13758" data-mp="31l6oopuaz2r9">
                <div className="font-medium" data-id="element-13759" data-mp="1h3ntblw5y65a">{tag.name}</div>
                <div className="text-xs opacity-80" data-id="element-13760" data-mp="1h58p1eu7rv4q">{tag.count} books</div>
              </div>
              {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-13761" data-mp="1q42hqu4fc1d0">{tag.description}</p>}
            </Link>)}
        </div>;
    }
    return <div className="space-y-3" data-id="element-13762" data-mp="1uwdfprfltd2x">
        {sortEntriesAlphabetically(Object.entries(displayGroupedTags)).map(([category, tags]) => <ContentAccordionSection key={category} title={category} itemCount={tags.length} data-id="element-13763" data-mp="24sq9epb5fqxo">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-13764" data-mp="28v3d786c5ert">
                {tags.map(tag => <Link key={tag.id} to={`/tagged-books/${tag.id}`} className={`${getSettingColor(tag.category)} rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer`} data-id="element-13765" data-mp="2dran1c1uraf1">
                    <div className="flex justify-between items-center mb-1" data-id="element-13766" data-mp="1mrcflg2rogua">
                      <div className="font-medium" data-id="element-13767" data-mp="1hs86epfuej3y">{tag.name}</div>
                      <div className="text-xs opacity-80" data-id="element-13768" data-mp="1vr7u9uvj6tz2">
                        {tag.count} books
                      </div>
                    </div>
                    {tag.description && <p className="text-xs mt-1 opacity-90" data-id="element-13769" data-mp="25pf9a869f1yf">
                        {tag.description}
                      </p>}
                  </Link>)}
              </div>
            </ContentAccordionSection>)}
      </div>;
  };
  // Sync selectedCategory from URL params reactively
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category || 'all');
  }, [searchParams]);
  return <div className={dk.page} data-id="element-13770" data-mp="2odsi4lcssnxv">
      <ContentPageHeader title="Settings" icon={getContentPageIcon('settings')} searchPlaceholder="Search settings..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-13771" data-mp="2jhbxw28pytq2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-13772" data-mp="2t4xp8arp2kk9">
        <div className="flex gap-8" data-id="element-13773" data-mp="2t6saxrasigpe">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-13774" data-mp="2y2ps1ztvnr38">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={() => {
            setSelectedCategory('all');
            setSortOption('alphabetical');
            setSearchQuery('');
          }} searchable={false} data-id="element-13775" data-mp="2p45hxn2dupqm" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-13776" data-mp="25e21boqd09ia">
            {sortedTags.length === 0 ? <div className="text-center py-12" data-id="element-13777" data-mp="1yac2xot41eyj">
                <p className={dk.textMuted} data-id="element-13778" data-mp="1tctzuf1rxtyg">
                  No settings found matching your search.
                </p>
              </div> : renderSettings()}
          </main>
        </div>
      </div>
    </div>;
};
export default Settings;